<script setup lang="ts">
/**
 * ConfirmDeleteModal — generic confirm-modal для destructive операций.
 * inbox-158 / docs/mockups/modal-confirm-delete.html 1:1.
 *
 * Универсальная: вызывается через modals.open('confirm-delete', payload),
 * где payload =
 *   {
 *     entityName: string,           // что удаляется ("vk-promo-spring")
 *     entityType: string,           // link / campaign / bio / qr / domain
 *     cascadeWarnings?: string[],   // последствия — список строк (опц)
 *     entityCreated?: string,       // human-readable метка "12 марта 2026"
 *     entityOwner?: string,         // human-readable владелец
 *     entityStats?: Array<{ label: string; value: string }>, // для resource-card
 *     archiveSupported?: boolean,   // показывать "архивировать вместо"
 *   }
 *
 * UX мокапа:
 *   • Resource preview card (имя, тип-tag, created/owner, stats)
 *   • Impact-list (cascadeWarnings)
 *   • Optional alt-card "Архивировать вместо" (если archiveSupported)
 *   • Type-to-confirm input — нужно ввести entityName точно
 *   • Notify-checkbox (для команды, no-op до backend)
 *   • Footer: Cancel + Delete (red, disabled до match)
 *
 * Caller pattern:
 *   modals.open('confirm-delete', { entityName: 'foo', entityType: 'link', ... });
 *   const off = modals.on('confirm-delete', async (result) => {
 *     // result.confirmed = true; this == DELETE confirmed
 *     await Api.delete(id);
 *   });
 *   // off() в onBeforeUnmount
 */

import { computed, ref } from 'vue';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiModalFooter from '@/components/ui/UiModalFooter.vue';

const modals = useModalStore();

interface Payload {
  entityName: string;
  entityType: string;
  cascadeWarnings?: string[];
  entityCreated?: string;
  entityOwner?: string;
  entityStats?: Array<{ label: string; value: string }>;
  archiveSupported?: boolean;
}

const payload = computed<Payload>(() => {
  const raw = (modals.payload || {}) as Record<string, unknown>;
  return {
    entityName: typeof raw.entityName === 'string' ? raw.entityName : '',
    entityType: typeof raw.entityType === 'string' ? raw.entityType : 'элемент',
    cascadeWarnings: Array.isArray(raw.cascadeWarnings) ? raw.cascadeWarnings.filter((x): x is string => typeof x === 'string') : [],
    entityCreated: typeof raw.entityCreated === 'string' ? raw.entityCreated : undefined,
    entityOwner: typeof raw.entityOwner === 'string' ? raw.entityOwner : undefined,
    entityStats: Array.isArray(raw.entityStats)
      ? (raw.entityStats as Array<{ label: string; value: string }>).filter(
          (s) => s && typeof s.label === 'string' && typeof s.value === 'string',
        )
      : [],
    archiveSupported: typeof raw.archiveSupported === 'boolean' ? raw.archiveSupported : false,
  };
});

const ENTITY_LABEL: Record<string, string> = {
  link: 'ссылку', campaign: 'кампанию', bio: 'bio-страницу',
  qr: 'QR-код', domain: 'домен', webhook: 'webhook',
  apikey: 'API-ключ', invitation: 'приглашение', user: 'участника',
};

const entityLabel = computed(() => ENTITY_LABEL[payload.value.entityType] || payload.value.entityType);
const headerTitle = computed(() =>
  `Удалить ${entityLabel.value} «${payload.value.entityName}»?`,
);

const confirmInput = ref('');
const notifyTeam = ref(false);

const isMatch = computed(() => confirmInput.value.trim() === payload.value.entityName);

async function copyName() {
  if (!navigator.clipboard?.writeText) return;
  try { await navigator.clipboard.writeText(payload.value.entityName); } catch { /* fail-quiet */ }
}

function archiveInstead() {
  modals.emit('confirm-delete', { confirmed: false, action: 'archive', entityType: payload.value.entityType });
  modals.close();
}

function confirmDelete() {
  if (!isMatch.value) return;
  modals.emit('confirm-delete', {
    confirmed: true,
    action: 'delete',
    entityType: payload.value.entityType,
    notifyTeam: notifyTeam.value,
  });
  modals.close();
}
</script>

<template>
  <UiModal :title="headerTitle" size="md" :disable-close="modals.pending" @close="modals.close">
    <template #header>
      <div class="head-row">
        <span class="danger-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/>
            <path d="M10 11v6M14 11v6"/>
            <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
          </svg>
        </span>
        <div>
          <h2 class="modal-title">{{ headerTitle }}</h2>
          <p class="modal-sub">
            Действие необратимо. Прочтите последствия ниже перед подтверждением.
          </p>
        </div>
      </div>
    </template>

    <!-- Resource preview -->
    <section class="resource-card">
      <div class="rc-icon">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 21h18M5 21V9l7-5 7 5v12M9 21v-6h6v6"/></svg>
      </div>
      <div class="rc-meta">
        <div class="rc-name">{{ payload.entityName }}</div>
        <div class="rc-sub">
          <UiBadge variant="default">{{ entityLabel }}</UiBadge>
          <template v-if="payload.entityCreated">
            <span class="bullet">·</span>
            <span>создана {{ payload.entityCreated }}</span>
          </template>
          <template v-if="payload.entityOwner">
            <span class="bullet">·</span>
            <span>владелец {{ payload.entityOwner }}</span>
          </template>
        </div>
        <div v-if="payload.entityStats && payload.entityStats.length" class="rc-stats">
          <div v-for="s in payload.entityStats" :key="s.label" class="rs">
            <b>{{ s.value }}</b> {{ s.label }}
          </div>
        </div>
      </div>
    </section>

    <!-- Impact / cascade warnings -->
    <section v-if="payload.cascadeWarnings && payload.cascadeWarnings.length" class="impact">
      <div class="impact-title">Что произойдёт</div>
      <ul class="impact-list">
        <li v-for="(w, i) in payload.cascadeWarnings" :key="i">
          <span class="ic-dot dot-bad"></span>
          <span>{{ w }}</span>
        </li>
      </ul>
    </section>

    <!-- Alternative: archive -->
    <section v-if="payload.archiveSupported" class="alt-card">
      <div class="alt-ico" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>
      </div>
      <div class="alt-text">
        <div class="alt-title">Лучше архивировать?</div>
        <div class="alt-sub">
          {{ entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1) }} скроется из активных, но
          останется доступна в архиве 90 дней — можно восстановить. Безопасный
          вариант если не уверены.
        </div>
      </div>
      <UiButton variant="outline" size="sm" @click="archiveInstead">Архивировать вместо</UiButton>
    </section>

    <!-- Confirm input -->
    <section class="confirm-section">
      <label class="field-label" for="confirm-name-input">
        Введите название для подтверждения
        <span class="hint-inline">— защита от случайного удаления</span>
      </label>
      <div class="confirm-input-wrap">
        <input
          id="confirm-name-input"
          v-model="confirmInput"
          class="input confirm-input"
          :class="{ matched: isMatch }"
          type="text"
          autocomplete="off"
          spellcheck="false"
          :placeholder="payload.entityName"
        />
        <span :class="['confirm-state', isMatch ? 'ok' : 'idle']" aria-hidden="true">
          <svg v-if="isMatch" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
          <svg v-else viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
        </span>
      </div>
      <div class="confirm-hint">
        Введите точно: <code>{{ payload.entityName }}</code>
        <button type="button" class="copy-name" title="Скопировать в буфер" @click="copyName">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
        </button>
      </div>
    </section>

    <!-- Notify team -->
    <section class="notify-section">
      <label class="check-row">
        <input type="checkbox" v-model="notifyTeam" />
        <span class="check-text">
          <span class="check-title">Уведомить команду об удалении</span>
          <span class="check-sub">Участники получат уведомление в чат (зависит от integrations настроек).</span>
        </span>
      </label>
    </section>

    <template #footer>
      <UiModalFooter
        save-variant="danger"
        :save-disabled="!isMatch || modals.pending"
        :cancel-disabled="modals.pending"
        @cancel="modals.close"
        @save="confirmDelete"
      >
        <template #save>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 6px;"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/></svg>
          Удалить {{ entityLabel }}
        </template>
      </UiModalFooter>
    </template>
  </UiModal>
</template>

<style scoped>
.head-row { display: flex; gap: 14px; align-items: flex-start; min-width: 0; }
.danger-icon {
  width: 38px; height: 38px; border-radius: 10px;
  background: rgba(255,93,143,0.12); color: #ff5d8f;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.modal-title { font-size: 17px; letter-spacing: -0.01em; margin: 0 0 4px; }
.modal-sub { font-size: 13px; color: var(--fg-muted, #9aa5b1); line-height: 1.5; margin: 0; }

/* Resource card */
.resource-card {
  display: flex; align-items: flex-start; gap: 14px;
  padding: 14px 16px;
  background: var(--bg-card, #1a1f29);
  border: 1px solid var(--border, #2a3340);
  border-radius: 10px;
  margin-bottom: 16px;
}
.rc-icon {
  width: 44px; height: 44px; border-radius: 10px;
  display: grid; place-items: center; color: #fff; flex-shrink: 0;
  background: linear-gradient(135deg, #4f8cff, #7c5cff);
  box-shadow: 0 4px 10px rgba(0,0,0,0.2);
}
.rc-meta { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; }
.rc-name {
  font-size: 14px; font-weight: 700; color: var(--fg, #e6eaf0);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  letter-spacing: -0.01em;
}
.rc-sub {
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  font-size: 12.5px; color: var(--fg-muted, #9aa5b1);
}
.bullet { color: var(--fg-dim, #6b7480); }
.rc-stats { display: flex; gap: 16px; flex-wrap: wrap; font-size: 12.5px; color: var(--fg-muted, #9aa5b1); }
.rc-stats b { color: var(--fg, #e6eaf0); font-weight: 600; }

/* Impact */
.impact { margin-bottom: 16px; }
.impact-title {
  font-size: 12.5px; font-weight: 600;
  color: var(--fg-muted, #9aa5b1);
  text-transform: uppercase; letter-spacing: 0.04em;
  margin-bottom: 8px;
}
.impact-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; }
.impact-list li {
  display: flex; gap: 10px; align-items: flex-start;
  font-size: 13px; line-height: 1.5;
  color: var(--fg, #e6eaf0);
}
.ic-dot {
  width: 8px; height: 8px; border-radius: 50%;
  margin-top: 6px; flex-shrink: 0;
}
.dot-bad { background: #ff5d8f; box-shadow: 0 0 6px rgba(255,93,143,0.5); }

/* Archive alternative */
.alt-card {
  display: flex; gap: 14px; align-items: flex-start;
  padding: 12px 14px;
  background: rgba(79,140,255,0.06);
  border: 1px solid rgba(79,140,255,0.3); border-radius: 8px;
  margin-bottom: 16px;
}
.alt-ico {
  width: 32px; height: 32px; border-radius: 7px;
  background: rgba(79,140,255,0.15); color: var(--accent-hi, #6f9eff);
  display: grid; place-items: center; flex-shrink: 0;
}
.alt-text { flex: 1; }
.alt-title { font-size: 13px; font-weight: 600; color: var(--fg, #e6eaf0); margin-bottom: 4px; }
.alt-sub { font-size: 12px; color: var(--fg-muted, #9aa5b1); line-height: 1.45; }

/* Confirm input */
.confirm-section { margin-bottom: 14px; }
.field-label { font-size: 13px; font-weight: 600; color: var(--fg, #e6eaf0); display: block; margin-bottom: 6px; }
.hint-inline { color: var(--fg-dim, #6b7480); font-weight: 400; }
.confirm-input-wrap { position: relative; }
.confirm-input {
  padding: 9px 38px 9px 12px;
  background: var(--bg, #0f1620);
  border: 1px solid var(--border, #2a3340);
  border-radius: 7px;
  color: var(--fg, #e6eaf0); font-size: 14px;
  font-family: var(--font-mono, ui-monospace, monospace);
  width: 100%; box-sizing: border-box;
}
.confirm-input.matched { border-color: #3dd87f; box-shadow: 0 0 0 1px rgba(61,216,127,0.3); }
.confirm-state {
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
}
.confirm-state.idle { color: var(--fg-dim, #6b7480); }
.confirm-state.ok { color: #3dd87f; }
.confirm-hint {
  font-size: 12px; color: var(--fg-muted, #9aa5b1);
  margin-top: 6px;
  display: flex; align-items: center; gap: 6px;
}
.confirm-hint code {
  font-family: var(--font-mono, ui-monospace, monospace);
  background: var(--bg, #0f1620); padding: 1px 6px; border-radius: 4px;
  color: var(--accent-hi, #6f9eff);
}
.copy-name {
  background: transparent; border: 0; color: var(--fg-muted, #9aa5b1);
  cursor: pointer; padding: 2px; border-radius: 4px;
  display: inline-flex; align-items: center;
}
.copy-name:hover { color: var(--fg, #e6eaf0); }

/* Notify team */
.notify-section { padding: 10px 0; border-top: 1px solid var(--border, #2a3340); }
.check-row { display: flex; gap: 10px; align-items: flex-start; cursor: pointer; }
.check-row input { margin-top: 3px; }
.check-text { display: flex; flex-direction: column; gap: 2px; }
.check-title { font-size: 13px; font-weight: 600; color: var(--fg, #e6eaf0); }
.check-sub { font-size: 12px; color: var(--fg-muted, #9aa5b1); line-height: 1.4; }
</style>
